/**
 * Adapters for Manifold's dashboard payloads.
 *
 * Every trend endpoint answers with a bare ARRAY, oldest first, zero-filled
 * across the whole window, and the `period` decides which key carries the
 * label. From app/routers/dashboard.py on prod:
 *
 *   invoices/billing?period=monthly   [{ month: "2026-09", total }]      x12
 *   invoices/billing?period=daily     [{ day: "2026-09-02", total }]     x32
 *   subscriptions/signups?monthly     [{ month, signup }]                x12
 *   subscriptions/signups?daily       [{ day, signup }]                  x32
 *   transactions/payments             [{ month, total }]                 x12
 *   transactions/refunds              [{ month, total }]                 x12
 *   subscriptions/counts              { total_subscriptions, subscriptions: [...] }
 *
 * Two things about these are easy to get wrong, and both were wrong on the
 * Payments page for as long as it has existed:
 *
 *   - They are arrays. `billing.total` and `payments.count` are undefined on
 *     every one of them, so the tiles reading those always showed "N/A" and the
 *     billing chart, gated on `billing.data`, never rendered at all.
 *   - The totals are DOLLARS. Manifold divides by 100 before responding, so
 *     anything that divides again reads a hundredth of the real number.
 *
 * These helpers exist so both facts are asserted in one place instead of
 * re-derived at each call site.
 */

/**
 * The period label on a row, whichever key it arrived under.
 * @param {{month?: string, day?: string}} row
 * @returns {string}
 */
export function periodLabel(row) {
  return row?.month ?? row?.day ?? ''
}

/**
 * A money series as the charts want it: `{ date, amount }`, amount in dollars.
 * Anything that is not an array (a failed endpoint arrives as null) becomes [].
 * @param {unknown} rows
 * @returns {Array<{date: string, amount: number}>}
 */
export function toAmountTrend(rows) {
  if (!Array.isArray(rows)) return []
  return rows.map(row => ({ date: periodLabel(row), amount: row?.total || 0 }))
}

/**
 * A signup series as the charts want it: `{ date, count }`.
 * @param {unknown} rows
 * @returns {Array<{date: string, count: number}>}
 */
export function toCountTrend(rows) {
  if (!Array.isArray(rows)) return []
  return rows.map(row => ({ date: periodLabel(row), count: row?.signup || 0 }))
}

/**
 * The current bucket — the last one, since every series is zero-filled up to
 * today. Deliberately not a sum: the tiles are labelled "This month" / "Today",
 * not "last 12 months".
 * @template T
 * @param {T[]} rows
 * @returns {T|null}
 */
export function currentBucket(rows) {
  return Array.isArray(rows) && rows.length > 0 ? rows[rows.length - 1] : null
}

/**
 * The current bucket's value under `key`, or null when there is no data —
 * distinct from 0, which is a real zero-filled month and should render as $0.
 * @param {unknown} rows
 * @param {string} key
 * @returns {number|null}
 */
export function currentValue(rows, key) {
  const bucket = currentBucket(Array.isArray(rows) ? rows : [])
  const value = bucket?.[key]
  return typeof value === 'number' ? value : null
}

/**
 * The bucket before the current one — the baseline every tile was missing.
 *
 * Deliberately the PREVIOUS bucket rather than an average of the series: the
 * tiles say "This month", and the question a reader has is "against last
 * month", not "against the last twelve".
 * @template T
 * @param {T[]} rows
 * @returns {T|null}
 */
export function priorBucket(rows) {
  return Array.isArray(rows) && rows.length > 1 ? rows[rows.length - 2] : null
}

/**
 * Percent change from the prior bucket to the current one, or null when it
 * cannot be stated.
 *
 * Null in two distinct cases that both have to stay null, because rendering
 * either as a number would be a claim:
 *   - there is no prior bucket, so there is nothing to compare against;
 *   - the prior bucket was ZERO, where percent change is undefined rather than
 *     infinite. Going from $0 to $5,000 is not "+∞%", it is a first month.
 *
 * The CURRENT bucket being zero is fine and reports -100%: that is a real and
 * quite alarming fact about this period.
 *
 * @param {unknown} rows
 * @param {string} key
 * @returns {number|null}
 */
export function periodDelta(rows, key) {
  const series = Array.isArray(rows) ? rows : []
  const current = currentBucket(series)?.[key]
  const prior = priorBucket(series)?.[key]
  if (typeof current !== 'number' || typeof prior !== 'number') return null
  if (prior === 0) return null
  return (100 * (current - prior)) / prior
}

/**
 * A ratio of two series' current buckets, as a percentage.
 *
 * Used for collection rate (collected over invoiced) and refund rate. Null
 * when the denominator is zero or absent — a rate out of nothing is not 0%.
 * @param {unknown} numeratorRows
 * @param {unknown} denominatorRows
 * @param {string} key
 * @returns {number|null}
 */
export function currentRatio(numeratorRows, denominatorRows, key) {
  const numerator = currentValue(numeratorRows, key)
  const denominator = currentValue(denominatorRows, key)
  if (typeof numerator !== 'number' || !denominator) return null
  return (100 * numerator) / denominator
}
